import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { fetchAdminStats } from '@/api/admin'
import { Users, Ticket, MessageSquare, Award } from 'lucide-react'

export function AdminStats() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: fetchAdminStats,
  })

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    )
  }

  const { partners = [], tickets = [], escalations, certifications } = data || {}

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10">
              <Users className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Partners</p>
              <p className="text-2xl font-bold">
                {partners.reduce((sum: number, p: any) => sum + (p.total || 0), 0)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-500/10">
              <Ticket className="h-6 w-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Open Tickets</p>
              <p className="text-2xl font-bold">
                {tickets.find((t: any) => t.status === 'open')?.count || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
              <MessageSquare className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending Escalations</p>
              <p className="text-2xl font-bold">
                {escalations?.unresolved || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
              <Award className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Certifications</p>
              <p className="text-2xl font-bold">
                {certifications?.completed || 0}/{certifications?.total || 0}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Partner Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Partner Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {partners.map((group: any) => (
              <div key={`${group.type}-${group.tier}`} className="flex items-center justify-between">
                <span className="text-sm">
                  {group.type} • {group.tier}
                </span>
                <span className="font-medium">{group.total}</span>
              </div>
            ))}
            {partners.length === 0 && (
              <p className="text-muted-foreground">No partner data available</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
